import type { DeliverableFile } from '@/domain/work-order.types'
import { useFileUpload } from '@/hooks/useFileUpload'
import { logger } from '@/lib/logger'
import { type DeliverableRow, mergeDeliverables } from '@/pages/WorkOrders/merge-deliverables'

/**
 * Starts uploads for one slot, and merges the in-flight ones into that slot's
 * server-backed list so both render as the same card.
 *
 * `owner` scopes the uploads to this slot's drop zone. The upload store is
 * global and every slot in the section mounts one, so an unscoped read would
 * make each slot display the others' uploads.
 */
export function useSlotUploadDriver(
	owner: string,
	mutateAsync: (variables: { file: File }) => Promise<unknown>,
	serverFiles: readonly DeliverableFile[],
) {
	const { items, retryConcern, removeItem } = useFileUpload(owner)

	const uploadAll = async (list: File[]) => {
		if (list.length === 0) return
		// Started together, not awaited in turn: every dropped file gets its card
		// immediately. Awaiting each one meant card n+1 didn't exist until n had
		// finished uploading, which also made the store's concurrency cap moot —
		// the cap is what limits parallel PUTs, so throughput is unchanged.
		await Promise.all(
			list.map(async (file) => {
				try {
					await mutateAsync({ file })
				} catch (error) {
					// The card shows the failure; this is for the log trail.
					logger.error('[deliverable-upload] file failed', { file: file.name, error })
				}
			}),
		)
	}

	// One list for the whole lifecycle. The upload row drops out the moment the
	// server lists the same fileId, which is the entire handover — no dismissal
	// call, no effect, and no window where the file is shown twice or not at all.
	const rows = mergeDeliverables(serverFiles, items)
	const cardProps = (row: DeliverableRow) => ({
		fileId: row.fileId,
		name: row.fileName,
		contentType: row.contentType,
		thumbnailUrl: row.thumbnailUrl ?? row.previewUrl,
		file: row.file,
		upload: row.upload,
		concerns: row.concerns,
		onRetryConcern: row.upload ? (id: string) => void retryConcern(row.upload?.itemId ?? '', id) : undefined,
		onDismiss: row.upload ? () => removeItem(row.upload?.itemId ?? '') : undefined,
	})

	return { rows, cardProps, uploadAll }
}
